import { useIsMutating, useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { BookOpen, Bookmark, Download, Play, Sparkles, Trash2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { keys } from '@/api/keys'
import { exportUrl, listStories, runtimeLabel, storyPartCount, storySeconds, streamReady, type StoryRow, type StorySort, type StoryStatus } from '@/api/stories'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Chip } from '@/components/ui/chip'
import { playIntentProps } from '@/components/StoryPlayer'
import { WithTooltip } from '@/components/ui/tooltip'
import { cn } from '@/components/ui/utils'
import { BOOKMARK_STORY_MUTATION, BookmarkButton } from '@/features/story/BookmarkButton'
import { DeleteStoryDialog, type StoryRef } from '@/features/story/DeleteStoryDialog'
import { StoryThumb } from '@/features/story/StoryThumb'
import { StoryModelLabel } from '@/features/story/StoryModelLabel'
import { GenerationModeBadge } from '@/features/story/GenerationModeBadge'
import { TopicBadge } from '@/features/story/TopicBadge'
import { Pagination } from '@/components/ui/pagination'

const toRef = ({ uid, title, status }: StoryRow): StoryRef => ({ uid, title, status })

/** "3 min 10 s · 5 parts" for a story that is made (the parts only for a longer one). */
export function storyLengthLine(story: Pick<StoryRow, 'status' | 'runtime_s' | 'parts'> & Partial<Pick<StoryRow, 'duration_s' | 'scenes'>>): string | null {
  if (story.status !== 'ready') return null
  const time = runtimeLabel(storySeconds(story))
  const count = storyPartCount(story)
  const parts = count > 1 ? `${count} parts` : null
  return [time, parts].filter(Boolean).join(' · ') || null
}

const STATUS_WORD: Partial<Record<StoryStatus, string>> = { ready: 'Ready', generating: 'Being made…', failed: 'Didn’t work', draft: 'Draft', archived: 'Put away' }
const STATUS_TONE: Partial<Record<StoryStatus, BadgeTone>> = { ready: 'ok', generating: 'info', failed: 'danger' }

export const DEFAULT_STORY_PAGE_SIZE = 12

/** `bookmarked`: only the bookmarked stories. */
export type StoryView = 'all' | 'bookmarked'
export interface StoryFilters {
  view?: StoryView
  sort?: StorySort
}

export interface StoryListProps extends StoryFilters {
  status?: string
  page?: number
  pageSize?: number
  onPageChange?: (page: number) => void
  /** Change the view or the order (the route keeps both in the URL). Without it the controls are hidden. */
  onFiltersChange?: (next: StoryFilters) => void
}

export function StoryList({
  status,
  view,
  sort,
  page: propPage,
  pageSize: customPageSize,
  onPageChange,
  onFiltersChange,
}: StoryListProps) {
  const bookmarkedView = view === 'bookmarked'
  // "Bookmarked first" only means something while every story is shown.
  const bookmarkedFirst = !bookmarkedView && sort === 'bookmarked'
  // `undefined`, not `false`, when off: the list key (and the request) stay what they were before bookmarks.
  const filters = { bookmarked: bookmarkedView ? true : undefined, sort: bookmarkedFirst ? ('bookmarked' as const) : undefined }
  // No poll while a bookmark is being saved: it could land before the save and flash the old state.
  const bookmarking = useIsMutating({ mutationKey: BOOKMARK_STORY_MUTATION }) > 0
  // Poll quickly only while a story is being made (its card fills in part by part); a quiet
  // library is looked at again every 30 s for stories made elsewhere.
  const query = useQuery({
    queryKey: keys.stories.list({ status, ...filters }),
    queryFn: ({ signal }) => listStories(status, signal, filters),
    refetchInterval: (q) => (bookmarking ? false : q.state.data?.items.some((s) => s.status === 'generating') ? 5000 : 30000),
  })
  const items = query.data?.items ?? []
  const showFilters = onFiltersChange !== undefined && (bookmarkedView || query.isLoading || items.length > 0)
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set())
  const [confirming, setConfirming] = useState<StoryRef[] | null>(null)
  // Said for a card that leaves the Bookmarked view (its own "Bookmark removed" leaves with it).
  const [announce, setAnnounce] = useState('')
  const headingRef = useRef<HTMLHeadingElement>(null)
  const selectAllRef = useRef<HTMLInputElement>(null)

  const [internalPage, setInternalPage] = useState(1)
  const pageSize = customPageSize ?? DEFAULT_STORY_PAGE_SIZE
  const currentPage = propPage ?? internalPage
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize))
  const safePage = query.isLoading
    ? Math.max(1, currentPage)
    : Math.min(Math.max(1, currentPage), totalPages)

  useEffect(() => {
    setInternalPage(1)
  }, [view, sort])

  useEffect(() => {
    if (!query.isLoading && query.data && currentPage > totalPages) {
      setInternalPage(totalPages)
      onPageChange?.(totalPages)
    }
  }, [currentPage, totalPages, query.isLoading, query.data, onPageChange])

  const handlePageChange = (newPage: number) => {
    const target = Math.min(Math.max(1, newPage), totalPages)
    setInternalPage(target)
    onPageChange?.(target)
    if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      } catch {
        // jsdom fallback
      }
    }
  }

  const pageStart = (safePage - 1) * pageSize
  const pageItems = items.slice(pageStart, pageStart + pageSize)

  // Derived from the rows on screen, so a story deleted (or filtered away) drops out of the count.
  const chosen = items.filter((s) => selected.has(s.uid))
  const pageChosen = pageItems.filter((s) => selected.has(s.uid))
  const allChosen = pageItems.length > 0 && pageChosen.length === pageItems.length

  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = pageChosen.length > 0 && !allChosen
  }, [pageChosen.length, allChosen])

  const toggle = (uid: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(uid)) next.delete(uid)
      else next.add(uid)
      return next
    })
  const toggleAll = () =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (allChosen) {
        for (const s of pageItems) next.delete(s.uid)
      } else {
        for (const s of pageItems) next.add(s.uid)
      }
      return next
    })
  const forget = (gone: StoryRef[]) =>
    setSelected((prev) => {
      const next = new Set(prev)
      for (const s of gone) next.delete(s.uid)
      return next
    })

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 pt-8 pb-28">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 ref={headingRef} tabIndex={-1} className="text-3xl font-semibold outline-none">
            Stories
          </h1>
          <p className="text-ink-2">Watch again, or make a new one.</p>
          <p className="sr-only" role="status" aria-live="polite">
            {announce}
          </p>
        </div>
        <Button asChild variant="primary">
          <Link to="/stories/new">
            <Sparkles aria-hidden />
            Make a story
          </Link>
        </Button>
      </header>

      {showFilters ? (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <div role="group" aria-label="Show" className="flex flex-wrap items-center gap-1.5">
            <Chip selected={!bookmarkedView} onClick={() => onFiltersChange?.({ view: undefined, sort })}>
              All stories
            </Chip>
            <Chip selected={bookmarkedView} icon={<Bookmark className="size-4" aria-hidden />} onClick={() => onFiltersChange?.({ view: 'bookmarked', sort })}>
              Bookmarked
            </Chip>
          </div>
          {bookmarkedView ? null : (
            <div role="group" aria-label="Order" className="flex flex-wrap items-center gap-1.5">
              <Chip size="sm" selected={!bookmarkedFirst} onClick={() => onFiltersChange?.({ view, sort: undefined })}>
                Newest first
              </Chip>
              <Chip size="sm" selected={bookmarkedFirst} onClick={() => onFiltersChange?.({ view, sort: 'bookmarked' })}>
                Bookmarked first
              </Chip>
            </div>
          )}
        </div>
      ) : null}

      {query.isLoading ? (
        <p>Loading stories…</p>
      ) : items.length ? (
        <>
          <div className="flex min-h-11 flex-wrap items-center gap-3">
            <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-ink-2">
              <input ref={selectAllRef} type="checkbox" className="size-4 accent-[var(--color-accent)]" checked={allChosen} onChange={toggleAll} />
              Select all
            </label>
            <span className="text-sm font-medium" aria-live="polite">
              {chosen.length ? `${chosen.length} selected` : ''}
            </span>
            {chosen.length ? (
              <>
                <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
                  Clear
                </Button>
                <Button variant="danger" size="sm" onClick={() => setConfirming(chosen.map(toRef))}>
                  <Trash2 aria-hidden />
                  Delete selected
                </Button>
              </>
            ) : null}
          </div>
          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" role="list">
            {pageItems.map((story) => {
              const isChosen = selected.has(story.uid)
              // A longer story still being made whose first part is out: it can be watched now.
              const watchable = story.status === 'generating' && streamReady(story)
              return (
                <li key={story.uid} className={cn('card flex flex-col overflow-hidden', isChosen && 'ring-2 ring-accent')} {...(story.status === 'ready' || watchable ? playIntentProps(story.uid) : {})}>
                  {story.status === 'ready' ? (
                    // The picture plays the story too; the Play button below is the keyboard's way in.
                    <Link to="/stories/$uid" params={{ uid: story.uid }} search={{ step: 'play' }} tabIndex={-1} aria-hidden className="group block">
                      <StoryThumb story={story} playable />
                    </Link>
                  ) : (
                    <StoryThumb story={story} />
                  )}
                  <div className="flex flex-1 flex-col gap-3 p-5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex flex-wrap gap-1.5">
                        <Badge tone={STATUS_TONE[story.status] ?? 'neutral'}>{STATUS_WORD[story.status] ?? story.status}</Badge>
                        <GenerationModeBadge mode={story.generation_mode} />
                        <TopicBadge story={story} />
                      </div>
                      <div className="flex items-center gap-3">
                        <BookmarkButton
                          story={story}
                          className="-my-2"
                          // In the Bookmarked view the card is about to leave: keep focus on the page.
                          onToggle={(on) => {
                            if (!bookmarkedView || on) return
                            setAnnounce(`Bookmark removed from “${story.title}”`)
                            headingRef.current?.focus()
                          }}
                        />
                        <label className="-m-2 inline-flex size-10 cursor-pointer items-center justify-center rounded-xl hover:bg-paper-2">
                          <input
                            type="checkbox"
                            className="size-4 accent-[var(--color-accent)]"
                            checked={isChosen}
                            onChange={() => toggle(story.uid)}
                            aria-label={`Select “${story.title}”`}
                          />
                        </label>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h2 className="text-xl font-semibold">{story.title}</h2>
                      <StoryModelLabel story={story} />
                      {storyLengthLine(story) ? (
                        <p className="mt-0.5 text-xs text-ink-3" data-testid="story-length">
                          {storyLengthLine(story)}
                        </p>
                      ) : null}
                      <p className="mt-1 line-clamp-3 text-sm text-ink-2">{story.prompt}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button asChild variant={story.status === 'ready' || watchable ? 'primary' : 'secondary'} size="md">
                        <Link to="/stories/$uid" params={{ uid: story.uid }} search={{ step: story.status === 'ready' || watchable ? 'play' : story.status === 'generating' ? 'generate' : undefined }}>
                          {story.status === 'ready' || watchable ? <Play aria-hidden /> : <BookOpen aria-hidden />}
                          {story.status === 'ready' ? 'Play' : watchable ? 'Preview' : story.status === 'failed' ? 'See what happened' : 'Open'}
                        </Link>
                      </Button>
                      {story.status === 'ready' ? (
                        <WithTooltip label="Download to keep (a .zip that plays offline)">
                          <Button asChild variant="ghost" size="sm" className="w-9 px-0 text-ink-2">
                            <a href={exportUrl(story.uid)} download aria-label={`Download “${story.title}”`}>
                              <Download aria-hidden />
                            </a>
                          </Button>
                        </WithTooltip>
                      ) : null}
                      <WithTooltip label="Delete">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="ml-auto w-9 px-0 text-ink-2 hover:bg-danger-soft hover:text-danger"
                          aria-label={`Delete “${story.title}”`}
                          onClick={() => setConfirming([toRef(story)])}
                        >
                          <Trash2 aria-hidden />
                        </Button>
                      </WithTooltip>
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>

          <Pagination
            page={safePage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            totalItems={items.length}
            pageSize={pageSize}
          />
        </>
      ) : bookmarkedView ? (
        <div className="card flex flex-col items-center gap-4 p-8 text-center text-ink-2" data-testid="no-bookmarks">
          <p>No bookmarked stories yet. Press the bookmark on a story to keep it here.</p>
          <Button variant="secondary" onClick={() => onFiltersChange?.({ view: undefined, sort })}>
            Show all stories
          </Button>
        </div>
      ) : (
        <div className="card flex flex-col items-center gap-4 p-8 text-center text-ink-2">
          <p>No stories yet. Pick a few friends and an idea — the studio does the rest.</p>
          <Button asChild variant="primary" size="lg">
            <Link to="/stories/new">
              <Sparkles aria-hidden />
              Make a story
            </Link>
          </Button>
        </div>
      )}

      <DeleteStoryDialog
        open={confirming !== null}
        onOpenChange={(open) => {
          if (!open) setConfirming(null)
        }}
        stories={confirming ?? []}
        onDeleted={(_count, gone) => forget(gone)}
        focusAfterDelete={() => headingRef.current}
      />
    </div>
  )
}
